/**
 * Color Utilities for Subgeo Neighborhood Mapping
 *
 * This module provides a deterministic, high-performance color generation system
 * for assigning unique colors to neighborhoods based on their subgeo association.
 *
 * Key Features:
 * - Deterministic color generation (same subgeo = same color across sessions)
 * - HSL-based palette avoiding green spectrum (120°-180°) used for selection
 * - High contrast and accessibility compliance
 * - Efficient memoization and caching
 * - Support for dynamic neighborhood reassignment
 */

import type { Subgeo } from '../types'

/**
 * Color pair representing fill and border colors for map styling
 */
export interface ColorPair {
	fill: string
	border: string
	fillOpacity: number
	borderOpacity: number
}

/**
 * Default color for neighborhoods not assigned to any subgeo
 * Light cream/beige to avoid grey spectrum
 */
export const DEFAULT_NEIGHBORHOOD_COLOR: ColorPair = {
	fill: '#ebe5d9',
	border: '#212121',
	fillOpacity: 0.7,
	borderOpacity: 1
}

/**
 * Color used for selected neighborhoods (to be avoided in subgeo colors)
 */
export const SELECTED_NEIGHBORHOOD_COLOR: ColorPair = {
	fill: '#10b981',
	border: '#212121',
	fillOpacity: 0.8,
	borderOpacity: 1
}

/**
 * Color used for detached neighborhoods (pending removal in edit mode)
 */
export const DETACHED_NEIGHBORHOOD_COLOR: ColorPair = {
	fill: '#fee2e2',
	border: '#212121',
	fillOpacity: 0.8,
	borderOpacity: 1
}

/**
 * Color generation constants
 * Protected ranges: Green (120-180°) for selection
 * Grey protection: Enforced via MIN_SATURATION (50%+)
 */
const COLOR_CONFIG = {
	SATURATION: 70, // Fixed saturation for color harmony
	LIGHTNESS: 50, // Fixed lightness for consistency
	BORDER_LIGHTNESS: 40, // Darker border for contrast
	GREEN_HUE_START: 120, // Protected for selected neighborhoods (teal/green)
	GREEN_HUE_END: 180, // End of protected green spectrum
	MIN_SATURATION: 50, // Minimum saturation to avoid grey-like colors
	TOTAL_HUE_RANGE: 360, // Full hue circle
	AVAILABLE_HUE_RANGE: 300, // Total available hues (360 - 60 green range)
	GOLDEN_RATIO_CONJUGATE: 0.618_033_988_749_895, // Golden ratio for optimal color spacing
	MIN_HUE_DIFFERENCE: 60, // Minimum hue difference for color distinction
	MIN_PERCEPTUAL_DISTANCE: 50, // Minimum perceptual distance between colors
	MAX_ATTEMPTS: 360, // Maximum attempts to find distinct color
	MAX_VARIATIONS: 16, // Maximum color variations to try
	PREDEFINED_PALETTE_SIZE: 25 // Number of predefined colors in palette
} as const

/**
 * Converts HSL values to hex color string
 *
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Hex color string (e.g., "#ff5733")
 */
function hslToHex(h: number, s: number, l: number): string {
	const hNormalized = h / 360
	const sNormalized = s / 100
	const lNormalized = l / 100

	const c = (1 - Math.abs(2 * lNormalized - 1)) * sNormalized
	const x = c * (1 - Math.abs(((hNormalized * 6) % 2) - 1))
	const m = lNormalized - c / 2

	let r = 0,
		g = 0,
		b = 0

	if (0 <= hNormalized && hNormalized < 1 / 6) {
		r = c
		g = x
		b = 0
	} else if (1 / 6 <= hNormalized && hNormalized < 2 / 6) {
		r = x
		g = c
		b = 0
	} else if (2 / 6 <= hNormalized && hNormalized < 3 / 6) {
		r = 0
		g = c
		b = x
	} else if (3 / 6 <= hNormalized && hNormalized < 4 / 6) {
		r = 0
		g = x
		b = c
	} else if (4 / 6 <= hNormalized && hNormalized < 5 / 6) {
		r = x
		g = 0
		b = c
	} else if (5 / 6 <= hNormalized && hNormalized < 1) {
		r = c
		g = 0
		b = x
	}

	const rHex = Math.round((r + m) * 255)
		.toString(16)
		.padStart(2, '0')
	const gHex = Math.round((g + m) * 255)
		.toString(16)
		.padStart(2, '0')
	const bHex = Math.round((b + m) * 255)
		.toString(16)
		.padStart(2, '0')

	return `#${rHex}${gHex}${bHex}`
}

/**
 * Converts hex color string to HSL values
 *
 * @param hex - Hex color string (e.g., "#ff5733")
 * @returns ColorVariation with hue, saturation, and lightness
 */
function hexToHsl(hex: string): ColorVariation {
	const r = Number.parseInt(hex.slice(1, 3), 16) / 255
	const g = Number.parseInt(hex.slice(3, 5), 16) / 255
	const b = Number.parseInt(hex.slice(5, 7), 16) / 255

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const diff = max - min

	let h = 0
	let s = 0
	const l = (max + min) / 2

	if (diff !== 0) {
		s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min)
		switch (max) {
			case r:
				h = (g - b) / diff + (g < b ? 6 : 0)
				break
			case g:
				h = (b - r) / diff + 2
				break
			case b:
				h = (r - g) / diff + 4
				break
			default:
				break
		}
		h /= 6
	}

	return {
		hue: Math.round(h * 360),
		saturation: Math.round(s * 100),
		lightness: Math.round(l * 100)
	}
}

/**
 * Generates a deterministic hash from a string (subgeo ID)
 *
 * @param str - String to hash (typically subgeo ID)
 * @returns Hash number
 */
function generateColorFromId(str: string): number {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		// biome-ignore lint/suspicious/noBitwiseOperators: bitwise operations intentional for hash generation
		hash = (hash << 5) - hash + str.charCodeAt(i)
		// biome-ignore lint/suspicious/noBitwiseOperators: bitwise operations intentional for hash generation
		hash &= hash
	}
	return Math.abs(hash)
}

/**
 * Checks if a hue falls within protected ranges
 * - Green (120-180°) is protected for selected neighborhoods
 * - Grey is naturally protected by maintaining high saturation (60%+)
 *
 * @param hue - Hue value to check (0-360)
 * @returns True if hue is in a protected range
 */
function isProtectedHue(hue: number): boolean {
	return (
		hue >= COLOR_CONFIG.GREEN_HUE_START && hue <= COLOR_CONFIG.GREEN_HUE_END
	)
}

/**
 * Color variation for enhanced distinction
 */
interface ColorVariation {
	hue: number
	saturation: number
	lightness: number
}

/**
 * Generates color variations for a base hue using saturation and lightness adjustments
 * Supports 20-50 subgeos with clearly distinguishable colors
 *
 * @param baseHue - Base hue value (0-360)
 * @param variationIndex - Index for variation (0-15)
 * @returns ColorVariation with adjusted saturation and lightness
 */
function generateColorVariation(
	baseHue: number,
	variationIndex: number
): ColorVariation {
	const saturations = [80, 55, 90, 65]
	const lightnesses = [50, 38, 62, 45]

	const satIndex = variationIndex % saturations.length
	const lightIndex =
		Math.floor(variationIndex / saturations.length) % lightnesses.length

	return {
		hue: baseHue,
		saturation: saturations[satIndex],
		lightness: lightnesses[lightIndex]
	}
}

/**
 * Calculates perceptual color distance between two HSL colors
 * Uses a simplified perceptual difference metric
 *
 * @param color1 - First color variation
 * @param color2 - Second color variation
 * @returns Perceptual distance value (higher = more different)
 */
function calculateColorDistance(
	color1: ColorVariation,
	color2: ColorVariation
): number {
	const hueDiff = Math.min(
		Math.abs(color1.hue - color2.hue),
		360 - Math.abs(color1.hue - color2.hue)
	)

	const satDiff = Math.abs(color1.saturation - color2.saturation)
	const lightDiff = Math.abs(color1.lightness - color2.lightness)

	return hueDiff * 3 + satDiff * 0.8 + lightDiff * 1.2
}

/**
 * Pre-defined color palette optimized for map visualization
 * Airbnb brand colors with maximum perceptual distinction
 * Covers first 25 subgeos
 * Note: Some colors may overlap with green selection spectrum
 */
const PREDEFINED_MAP_PALETTE: ColorVariation[] = [
	{ hue: 207, saturation: 70, lightness: 41 },
	{ hue: 28, saturation: 100, lightness: 53 },
	{ hue: 100, saturation: 57, lightness: 40 },
	{ hue: 360, saturation: 69, lightness: 50 },
	{ hue: 271, saturation: 42, lightness: 57 },
	{ hue: 10, saturation: 30, lightness: 42 },
	{ hue: 51, saturation: 80, lightness: 49 },
	{ hue: 318, saturation: 66, lightness: 68 },
	{ hue: 213, saturation: 15, lightness: 49 },
	{ hue: 207, saturation: 80, lightness: 45 },
	{ hue: 130, saturation: 61, lightness: 70 },
	{ hue: 6, saturation: 100, lightness: 80 },
	{ hue: 261, saturation: 51, lightness: 78 },
	{ hue: 28, saturation: 100, lightness: 73 },
	{ hue: 60, saturation: 62, lightness: 71 },
	{ hue: 207, saturation: 20, lightness: 78 },
	{ hue: 213, saturation: 56, lightness: 79 },
	{ hue: 164, saturation: 71, lightness: 36 },
	{ hue: 18, saturation: 98, lightness: 42 },
	{ hue: 247, saturation: 39, lightness: 57 },
	{ hue: 330, saturation: 78, lightness: 54 },
	{ hue: 81, saturation: 61, lightness: 38 },
	{ hue: 46, saturation: 97, lightness: 46 },
	{ hue: 28, saturation: 39, lightness: 44 },
	{ hue: 0, saturation: 0, lightness: 40 }
]

/**
 * Generates a color for a single subgeo, avoiding collisions with existing colors
 *
 * Two-tier approach:
 * 1. Use pre-defined palette for first 25 subgeos (guaranteed distinct colors)
 * 2. Fall back to golden ratio algorithm for additional subgeos
 *
 * Features:
 * - Deterministic: Same subgeo ID produces same color
 * - Optimized: Pre-defined colors for common case (≤25 subgeos)
 * - Scalable: Algorithmic generation for growth beyond 25
 * - Uses Airbnb brand colors (some may overlap with green selection spectrum)
 *
 * @param subgeoId - ID of the subgeo
 * @param existingColors - Map of already assigned colors
 * @param existingVariations - Optional cached HSL variations for performance
 * @returns ColorPair for the new subgeo
 */
/**
 * Finds the minimum distance of a color variation from all existing variations.
 * Returns { minDistance, isAcceptable } where isAcceptable means all distances
 * exceed MIN_PERCEPTUAL_DISTANCE.
 */
function evaluateVariation(
	variation: ColorVariation,
	variations: ColorVariation[]
): { minDistance: number; isAcceptable: boolean } {
	let minDistance = Number.POSITIVE_INFINITY
	for (const existing of variations) {
		const distance = calculateColorDistance(variation, existing)
		if (distance < COLOR_CONFIG.MIN_PERCEPTUAL_DISTANCE) {
			return {
				minDistance: Math.min(minDistance, distance),
				isAcceptable: false
			}
		}
		minDistance = Math.min(minDistance, distance)
	}
	return { minDistance, isAcceptable: true }
}

/**
 * Tries all variation indices for a given hue and returns the best match.
 */
function findBestVariationForHue(
	currentHue: number,
	variations: ColorVariation[],
	currentBest: { variation: ColorVariation | null; distance: number }
): { variation: ColorVariation | null; distance: number; found: boolean } {
	let bestVariation = currentBest.variation
	let bestDistance = currentBest.distance

	for (let varIndex = 0; varIndex < COLOR_CONFIG.MAX_VARIATIONS; varIndex++) {
		const variation = generateColorVariation(currentHue, varIndex)
		const { minDistance, isAcceptable } = evaluateVariation(
			variation,
			variations
		)

		if (isAcceptable) {
			return { variation, distance: minDistance, found: true }
		}

		if (minDistance > bestDistance) {
			bestDistance = minDistance
			bestVariation = variation
		}
	}

	return { variation: bestVariation, distance: bestDistance, found: false }
}

export function generateSingleSubgeoColor(
	subgeoId: string,
	existingColors: Map<string, ColorPair>,
	existingVariations?: ColorVariation[]
): ColorPair {
	const subgeoIndex = existingColors.size

	if (subgeoIndex < COLOR_CONFIG.PREDEFINED_PALETTE_SIZE) {
		const predefinedColor = PREDEFINED_MAP_PALETTE[subgeoIndex]
		const fillColor = hslToHex(
			predefinedColor.hue,
			predefinedColor.saturation,
			predefinedColor.lightness
		)

		return {
			fill: fillColor,
			border: '#212121',
			fillOpacity: 0.9,
			borderOpacity: 1
		}
	}

	const variations =
		existingVariations ??
		Array.from(existingColors.values()).map((colorPair) =>
			hexToHsl(colorPair.fill)
		)

	const hash = generateColorFromId(subgeoId)
	const sequenceNumber = hash % 1000

	const goldenAngle =
		COLOR_CONFIG.GOLDEN_RATIO_CONJUGATE * COLOR_CONFIG.TOTAL_HUE_RANGE
	let baseHue = (sequenceNumber * goldenAngle) % COLOR_CONFIG.TOTAL_HUE_RANGE

	if (isProtectedHue(baseHue)) {
		baseHue =
			(baseHue +
				(COLOR_CONFIG.GREEN_HUE_END - COLOR_CONFIG.GREEN_HUE_START)) %
			COLOR_CONFIG.TOTAL_HUE_RANGE
	}

	let bestVariation: ColorVariation | null = null
	let bestDistance = 0

	for (
		let hueAttempt = 0;
		hueAttempt < COLOR_CONFIG.MAX_ATTEMPTS;
		hueAttempt++
	) {
		const currentHue =
			(baseHue + hueAttempt * COLOR_CONFIG.MIN_HUE_DIFFERENCE) %
			COLOR_CONFIG.TOTAL_HUE_RANGE

		if (isProtectedHue(currentHue)) {
			continue
		}

		const result = findBestVariationForHue(currentHue, variations, {
			variation: bestVariation,
			distance: bestDistance
		})
		bestVariation = result.variation
		bestDistance = result.distance

		if (result.found) {
			break
		}

		if (
			bestVariation &&
			bestDistance >= COLOR_CONFIG.MIN_PERCEPTUAL_DISTANCE
		) {
			break
		}
	}

	if (!bestVariation) {
		bestVariation = generateColorVariation(baseHue, 0)
	}

	const fillColor = hslToHex(
		bestVariation.hue,
		bestVariation.saturation,
		bestVariation.lightness
	)

	return {
		fill: fillColor,
		border: '#212121',
		fillOpacity: 0.8,
		borderOpacity: 1
	}
}

/**
 * Generates a deterministic color palette for multiple subgeos
 *
 * Two-tier approach:
 * - First 25 subgeos: Airbnb brand colors for maximum distinction
 * - Additional subgeos: Golden ratio-based algorithmic generation
 *
 * Features:
 * - Deterministic: Same subgeo order produces same colors
 * - Optimized: Pre-defined palette covers common use cases (≤25 subgeos)
 * - Scalable: Algorithmic fallback for growth beyond 25
 * - Accessible: High contrast between fill and border
 * - Brand compliant: Uses Airbnb colors (some may overlap with green selection spectrum)
 *
 * @param subgeos - Array of subgeo objects
 * @returns Map of subgeoId to ColorPair
 */
export function generateSubgeoColors(
	subgeos: Subgeo[]
): Map<string, ColorPair> {
	const colorMap = new Map<string, ColorPair>()
	const variations: ColorVariation[] = []

	const sortedSubgeos = [...subgeos].sort((a, b) => a.id.localeCompare(b.id))

	for (const subgeo of sortedSubgeos) {
		const color = generateSingleSubgeoColor(subgeo.id, colorMap, variations)
		colorMap.set(subgeo.id, color)
		variations.push(hexToHsl(color.fill))
	}

	return colorMap
}

/**
 * Creates a neighborhood-to-color mapping based on subgeo assignments
 *
 * Performance optimizations:
 * - O(1) lookup time for neighborhood colors
 * - Cached until subgeo assignments change
 * - Memory efficient with Map data structure
 *
 * @param subgeos - Array of subgeo objects with neighborhood assignments
 * @param subgeoColors - Map of subgeo colors from generateSubgeoColors
 * @returns Map of neighborhoodId to fill color string
 */
export function createNeighborhoodColorMap(
	subgeos: Subgeo[],
	subgeoColors: Map<string, ColorPair>
): Map<string, string> {
	const neighborhoodColorMap = new Map<string, string>()

	for (const subgeo of subgeos) {
		const colorPair = subgeoColors.get(subgeo.id)
		if (colorPair) {
			for (const neighborhoodId of subgeo.neighborhoodIds) {
				neighborhoodColorMap.set(neighborhoodId, colorPair.fill)
			}
		}
	}

	return neighborhoodColorMap
}

/**
 * Gets the appropriate color for a neighborhood based on its assignment and selection state
 *
 * Priority order:
 * 1. Selected neighborhoods: Use selection color (green/teal)
 * 2. Assigned neighborhoods: Use subgeo color
 * 3. Unassigned neighborhoods: Use default blue color
 *
 * @param neighborhoodId - ID of the neighborhood
 * @param isSelected - Whether the neighborhood is currently selected
 * @param neighborhoodColorMap - Map of neighborhood colors from createNeighborhoodColorMap
 * @returns ColorPair for styling the neighborhood
 */
export function getNeighborhoodColor(
	neighborhoodId: string,
	isSelected: boolean,
	neighborhoodColorMap: Map<string, string>
): ColorPair {
	// Selection state takes priority
	if (isSelected) {
		return SELECTED_NEIGHBORHOOD_COLOR
	}

	const assignedColor = neighborhoodColorMap.get(neighborhoodId)
	if (assignedColor) {
		return {
			fill: assignedColor,
			border: '#212121',
			fillOpacity: 0.7,
			borderOpacity: 1
		}
	}

	// Default color for unassigned neighborhoods
	return DEFAULT_NEIGHBORHOOD_COLOR
}

/**
 * Generates a hash of subgeo array for memoization purposes
 *
 * @param subgeos - Array of subgeo objects
 * @returns Hash string for memoization key
 */
export function generateSubgeoHash(subgeos: Subgeo[]): string {
	return subgeos
		.map((s) => `${s.id}:${s.neighborhoodIds.sort().join(',')}`)
		.sort()
		.join('|')
}
